/* eslint-disable no-console */
import axios from 'axios';
import store from '../redux/store';
import { getFirebaseCustomerId } from '../api/firebase/firebaseAuthentication';
import {
  API_STREAM_LOGS,
  FRONTEND_STACK,
  LEVELS,
  SEVERITY,
  API_GET_IP_ADDRESS,
} from '../constants';

import _ from 'lodash';
import pckg from '../../package.json';
import { isDev, isQrCode } from './config';
let globalIpAddress: string | null = null;
let ipPromise: Promise<string | null> | null = null;
interface FunctionInfo {
  fonctionName?: string;
  descriptionFonction?: string;
  paramsFonction?: Record<string, any>;
}

interface LogEventParams {
  level?: string;
  message: string;
  severity?: string;
  errorCode?: number | null;
  data?: {
    companyId?: string;
    shopId?: string;
    chargeId?: string
  };
  contexte?: {
    apiEndpoint?: string;
    location?: string;
    componentStack?: string;
    [key: string]: any;
  };
  fonction?: FunctionInfo;
}

interface LogPayload {
  timestamp: string;
  level: string;
  severity: string | null;
  message: string;
  errorCode: number | null;
  traceId: string;
  data: {
    companyId: string | null;
    shopId: string | null;
    chargeId?: string | null;
  };
  contexte: {
    version: string;
    stack: string;
    apiEndpoint?: string;
    location?: string;
    componentStack?: string;
    [key: string]: any;
  };
  fonction?: FunctionInfo;
}

export const logLowLog = (
  message: string,
  data = {},
  errorCode = null,
  contexte = {},
  fonction = null,
) =>
  logEvent({
    message,
    severity: SEVERITY.LOW,
    errorCode,
    data,
    contexte,
    fonction,
  });

export const logMediumLog = (
  message: string,
  data = {},
  errorCode = null,
  contexte = {},
  fonction = null,
) =>
  logEvent({
    message,
    severity: SEVERITY.MEDIUM,
    errorCode,
    data,
    contexte,
    fonction,
  });

export const logHighLog = (
  message: string,
  data = {},
  errorCode = null,
  contexte = {},
  fonction = null,
) =>
  logEvent({
    message,
    severity: SEVERITY.HIGH,
    errorCode,
    data,
    contexte,
    fonction,
  });

export const logLowError = (
  message: string,
  data = {},
  errorCode = null,
  contexte = {},
  fonction = null,
) =>
  logEvent({
    level: LEVELS.ERROR,
    message,
    severity: SEVERITY.LOW,
    errorCode,
    data,
    contexte,
    fonction,
  });

export const logMediumError = (
  message: string,
  data = {},
  errorCode = null,
  contexte = {},
  fonction = null,
) =>
  logEvent({
    level: LEVELS.ERROR,
    message,
    severity: SEVERITY.MEDIUM,
    errorCode,
    data,
    contexte,
    fonction,
  });

export const logHighError = (
  message: string,
  data = {},
  errorCode = null,
  contexte = {},
  fonction = null,
) =>
  logEvent({
    level: LEVELS.ERROR,
    message,
    severity: SEVERITY.HIGH,
    errorCode,
    data,
    contexte,
    fonction,
  });

export const fetchGlobalIpAddress = async (): Promise<string | null> => {
  if (globalIpAddress) return globalIpAddress;
  if (ipPromise) return ipPromise;

  ipPromise = (async () => {
    try {
      const response = await fetch(API_GET_IP_ADDRESS, {
        method: 'GET',
      });
      const data = await response.json();
      globalIpAddress = data.ip;
      return globalIpAddress;
    } catch (err) {
      logLowError(
        'Impossible de récupérer l’adresse IP globale',
        {},
        null,
        {},
        null,
      );
      return null;
    } finally {
      ipPromise = null;
    }
  })();

  return ipPromise;
};

export const logEvent = async ({
  level = LEVELS.INFO,
  message,
  severity = '',
  errorCode = null,
  data = {},
  contexte = {},
  fonction,
}: LogEventParams): Promise<void> => {
  const { companyId } = store.getState().configurationReducer || {};
  const { shopId } = store.getState().pendingOrderReducer || {};
  const { isUserAnonymous } = store.getState().userReducer || {};
  const traceId = getFirebaseCustomerId();
  const ip = globalIpAddress || (await fetchGlobalIpAddress());

  const logTypePrefix =
    level === LEVELS.INFO
      ? '[LOG_INFO_WEBAPP]'
      : `[${severity.toUpperCase()}_LOG_ERROR_WEBAPP]`;

  const logPayload: LogPayload = {
    timestamp: new Date().toISOString(),
    level,
    severity,
    message: `${logTypePrefix} - ${isQrCode() ? 'QRCODE' : 'WEB'} - ${message}`,
    errorCode,
    traceId,
    data: {
      companyId: data.companyId || companyId || null,
      shopId: data.shopId || shopId || null,
      chargeId: data.chargeId || null,
      ...data,
    },
    contexte: {
      ...contexte,
      version: pckg.version,
      stack: contexte.stack || FRONTEND_STACK,
      ipAddress: ip,
      initiatorRole: isUserAnonymous ? 'Anonymous user' : 'Connected user',
      initiatorId: traceId,
      traceId,
      webAppUrl: window?.location?.hostname ?? null,
      env: isDev() ? 'DEV' : 'PROD',
    },
  };

  if (fonction && !_.isEmpty(fonction)) {
    logPayload.fonction = fonction;
  }

  try {
    const logOutput = JSON.stringify(logPayload, null, 4);

    if (!window?.location?.hostname?.includes('localhost')) {
      await axios.post(API_STREAM_LOGS, { message: logOutput });
    }

    if (level === LEVELS.ERROR) {
      console.error(logOutput);
    }
  } catch (error) {
    console.error('Error streaming logs to cloudwatch', {
      error: error instanceof Error ? error.message : 'Unknown error',
      originalLog: logPayload,
    });
  }
};
